import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, CreditCard, ShieldCheck, Building, Loader2, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';

export default function Banks() {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newBank, setNewBank] = useState({ bankName: '', branch: '', accountNumber: '', accountHolder: '' });

  useEffect(() => {
    if (userData?.banks) setBanks(userData.banks);
  }, [userData]);

  const handleAddBank = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updated = [...banks, { ...newBank, id: Date.now() }];
      await setDoc(doc(db, 'users', currentUser.uid), { banks: updated }, { merge: true });
      setBanks(updated);
      setShowAdd(false);
      setNewBank({ bankName: '', branch: '', accountNumber: '', accountHolder: '' });
    } catch (err) {
      console.error(err);
      alert('Failed to save bank account.');
    } finally {
      setLoading(false);
    }
  };

  const deleteBank = async (id) => {
    if (!window.confirm('Remove this bank account?')) return;
    try {
      const updated = banks.filter(b => b.id !== id);
      await setDoc(doc(db, 'users', currentUser.uid), { banks: updated }, { merge: true });
      setBanks(updated);
    } catch {
      alert('Failed to remove account.');
    }
  };

  return (
    <div className="min-h-screen bg-stripe-bg pb-24">
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="btn-icon">
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-stripe-dark tracking-tight">Accounts</h1>
              <p className="text-xs text-stripe-light font-medium hidden sm:block">Manage your linked accounts</p>
            </div>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary text-xs px-4 py-2 gap-1.5">
            <Plus size={15} />
            Add account
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {banks.length === 0 ? (
          <div className="s-card">
            <div className="empty-state">
              <div className="w-14 h-14 bg-stripe-bg rounded-2xl flex items-center justify-center mb-4 border border-stripe-border">
                <CreditCard size={24} className="text-stripe-light" />
              </div>
              <h3 className="text-base font-semibold text-stripe-dark mb-1">No bank accounts yet</h3>
              <p className="text-sm text-stripe-slate mb-4">Add a bank account to receive loan disbursements.</p>
              <button onClick={() => setShowAdd(true)} className="btn-primary text-xs">
                <Plus size={14} /> Add bank account
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {banks.map(bank => (
              <div key={bank.id} className="s-card flex items-center justify-between gap-4 hover:border-stripe-dark/20 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-stripe-bg rounded-xl flex items-center justify-center border border-stripe-border shrink-0">
                    <Building size={20} className="text-stripe-slate" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stripe-dark">{bank.bankName}</p>
                    <p className="text-xs text-stripe-slate font-medium">{bank.branch}</p>
                    <p className="text-xs font-mono text-stripe-slate mt-0.5 tracking-wider">{bank.accountNumber}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="hidden sm:flex items-center gap-1.5 badge badge-success">
                    <ShieldCheck size={11} />
                    <span>Verified</span>
                  </div>
                  <button onClick={() => deleteBank(bank.id)} className="btn-icon text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Bank Modal */}
      {showAdd && (
        <div className="s-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="s-modal max-h-[95vh]">
            <div className="s-modal-header">
              <div>
                <h3 className="text-base font-bold text-stripe-dark">Add bank account</h3>
                <p className="text-xs text-stripe-slate mt-0.5">Link a new disbursement account</p>
              </div>
              <button onClick={() => setShowAdd(false)} className="btn-icon">✕</button>
            </div>
            <form onSubmit={handleAddBank} className="s-modal-body space-y-4">
              <div>
                <label className="s-label">Bank name</label>
                 <select
                   className="s-select"
                   value={newBank.bankName}
                   onChange={(e) => setNewBank({ ...newBank, bankName: e.target.value })}
                   required
                 >
                    <option value="" disabled>Select a Bank</option>
                    <option value="Commercial Bank">Commercial Bank</option>
                    <option value="Bank of Ceylon">Bank of Ceylon (BOC)</option>
                    <option value="Hatton National Bank">Hatton National Bank (HNB)</option>
                    <option value="Sampath Bank">Sampath Bank</option>
                    <option value="People's Bank">People's Bank</option>
                    <option value="National Development Bank">National Development Bank (NDB)</option>
                    <option value="Nations Trust Bank">Nations Trust Bank (NTB)</option>
                    <option value="Seylan Bank">Seylan Bank</option>
                    <option value="DFCC Bank">DFCC Bank</option>
                    <option value="Pan Asia Bank">Pan Asia Bank</option>
                    <option value="Union Bank">Union Bank</option>
                    <option value="Amana Bank">Amana Bank</option>
                    <option value="Sanasa Development Bank">Sanasa Development Bank (SDB)</option>
                    <option value="Cargills Bank">Cargills Bank</option>
                    <option value="Standard Chartered Bank">Standard Chartered Bank</option>
                    <option value="HSBC">HSBC</option>
                 </select>
              </div>
              <div>
                <label className="s-label">Branch</label>
                <input
                  type="text"
                  className="s-input"
                  placeholder="e.g. Colombo Fort"
                  value={newBank.branch}
                  onChange={(e) => setNewBank({ ...newBank, branch: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="s-label">Account number</label>
                <input
                  type="text"
                  className="s-input font-mono tracking-wider"
                  placeholder="XXXX XXXX XXXX"
                  value={newBank.accountNumber}
                  onChange={(e) => setNewBank({ ...newBank, accountNumber: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="s-label">Account holder name</label>
                <input
                  type="text"
                  className="s-input"
                  placeholder="Name as on passbook"
                  value={newBank.accountHolder}
                  onChange={(e) => setNewBank({ ...newBank, accountHolder: e.target.value })}
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
                  {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                  {loading ? 'Saving…' : 'Save account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
